/**
 * LiveStatusBadge — one consistent chip for a machine's real-time status
 * (Engine On / Ignition On / GPS On / Vehicle Off), used on the fleet list,
 * map popups and the machine detail page.
 *
 * Pass either a canonical/legacy `status` string or a `device` object (it will
 * resolve the status, applying client-side staleness). A pulsing dot indicates
 * a live (non-OFF) state.
 */
import React from 'react';
import { Chip, Box, Tooltip } from '@mui/material';
import { STATUS_META, normalizeStatus, resolveDeviceStatus, statusLabel } from '../../utils/liveStatus';

const Dot = ({ hex, pulse }) => (
    <Box
        sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: hex,
            ml: 0.6,
            ...(pulse && {
                animation: 'liveStatusPulse 1.6s ease-in-out infinite',
                '@keyframes liveStatusPulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.45, transform: 'scale(1.35)' },
                },
            }),
        }}
    />
);

const LiveStatusBadge = ({ status, device, size = 'small', showDot = true, lastSeen, sx }) => {
    const code = device ? resolveDeviceStatus(device) : normalizeStatus(status);
    const meta = STATUS_META[code] || STATUS_META.OFF;
    const isLive = code !== 'OFF';

    const seen = lastSeen || device?.__liveTs || device?.last_seen || device?.recorded_at;
    const chip = (
        <Chip
            icon={showDot ? <Dot hex={meta.hex} pulse={isLive} /> : undefined}
            label={meta.label}
            size={size}
            color={meta.color}
            variant={isLive ? 'filled' : 'outlined'}
            sx={{
                fontWeight: 700,
                fontSize: size === 'small' ? '0.7rem' : '0.8rem',
                borderRadius: '6px',
                '& .MuiChip-icon': { ml: 0.5 },
                ...sx,
            }}
        />
    );

    if (!seen) return chip;
    let when;
    try { when = new Date(seen).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }); } catch { when = String(seen); }
    return <Tooltip title={`${statusLabel(code)} • last frame ${when}`}>{chip}</Tooltip>;
};

export default LiveStatusBadge;
